/**
 * Génération des mockups à la validation d'une création — logique PURE.
 *
 * CE QUE C'EST
 * ------------
 * À la validation, en plus des fichiers d'impression, on peut produire des
 * MOCKUPS : la création du client posée sur des photos du produit réel (un
 * t-shirt porté, un mug…), une image par vue, composées côté serveur à partir de
 * la mise en scène réglée au back-office.
 *
 * DEUX INTERRUPTEURS, ET C'EST VOULU
 * ----------------------------------
 *  1. le PRODUIT (`product.mockupEnabled`, réglé au back-office) : tous les
 *     produits n'ont pas de photo de mise en scène ;
 *  2. la SESSION (storefront) : `sendEcommerceProduct.mockupEnabled`, le message
 *     `{ eventType: 'mockup' }` ou le param d'URL `?mockup=1|0`, à défaut le
 *     réglage du site (`exportFiles.mockups.mustBeExported`).
 *
 * Les deux doivent être vrais. Le premier dit « ce produit SAIT produire des
 * mockups », le second « CE client, sur CETTE boutique, en veut ». Sans le
 * second, une boutique qui n'exploite pas les mockups paierait un rendu serveur
 * par commande pour rien.
 *
 * Module SANS DOM ni store, pour rester testable (cf. mockupExport.spec.ts) —
 * c'est le pattern du projet : extraire la logique, tester l'extrait.
 */

/** Côté maximal du visuel envoyé au rendu, en pixels. */
export const MOCKUP_ARTWORK_MAX_EDGE = 1600;

export interface MockupExportContext {
    /**
     * Demande explicite de l'hôte : `true` / `false` s'il s'est prononcé,
     * `null` s'il n'a rien dit (on retombe alors sur le réglage du site).
     */
    hostRequested: boolean | null;
    /** Réglage du site : `exportFiles.mockups.mustBeExported`. */
    siteEnabled: boolean;
    /** Réglage du produit au back-office (`Product.mockupEnabled`). */
    productEnabled: boolean;
}

/**
 * Faut-il générer les mockups pour cette validation ?
 *
 * Le produit a un DROIT DE VETO : quoi que demande l'hôte, un produit sans mise
 * en scène ne produira rien — autant ne pas appeler le serveur.
 */
export function isMockupExportEnabled(context: MockupExportContext): boolean {
    if (!context.productEnabled) {
        return false;
    }

    return context.hostRequested ?? context.siteEnabled;
}

/**
 * Interprète un paramètre d'URL `?mockup=…`.
 *
 * `null` = le paramètre est absent ou illisible : l'hôte ne s'est pas prononcé,
 * ce qui n'est PAS la même chose que « désactivé ».
 */
export function parseMockupQueryFlag(value: unknown): boolean | null {
    if (value === undefined || value === null) {
        return null;
    }

    const raw = String(Array.isArray(value) ? value[0] : value)
        .trim()
        .toLowerCase();

    if (raw === '1' || raw === 'true' || raw === 'yes') {
        return true;
    }
    if (raw === '0' || raw === 'false' || raw === 'no') {
        return false;
    }

    return null;
}

/**
 * Dimensions du visuel envoyé au rendu, bornées au côté maximal.
 *
 * Un fichier d'impression fait couramment 4000 px de côté ; un mockup est une
 * image de communication qui n'en tirerait aucun bénéfice, et le client final
 * paierait l'envoi de plusieurs Mo par zone au moment du paiement. Le RATIO est
 * conservé : c'est lui qui garantit que le motif n'est pas déformé.
 */
export function mockupArtworkSize(
    width: number,
    height: number,
    maxEdge: number = MOCKUP_ARTWORK_MAX_EDGE
): { width: number; height: number } {
    const safeWidth = Number.isFinite(width) && width > 0 ? width : maxEdge;
    const safeHeight = Number.isFinite(height) && height > 0 ? height : maxEdge;
    const longest = Math.max(safeWidth, safeHeight);

    if (!(maxEdge > 0) || longest <= maxEdge) {
        return { width: Math.round(safeWidth), height: Math.round(safeHeight) };
    }

    const scale = maxEdge / longest;

    return {
        width: Math.max(1, Math.round(safeWidth * scale)),
        height: Math.max(1, Math.round(safeHeight * scale))
    };
}

/**
 * Un mockup rendu par le serveur, tel qu'il est remonté au storefront.
 *
 * Forme STABLE du contrat d'intégration : le serveur peut enrichir sa réponse
 * sans que la boutique ait à s'adapter, et un champ inconnu vaut `null` plutôt
 * que d'être absent — l'intégrateur lit toujours les mêmes clés.
 */
export interface GeneratedMockup {
    /** Vue mise en scène au back-office (identifiant). */
    viewId: string | null;
    /** Libellé de la vue (« Face », « Dos »…). */
    name: string | null;
    /**
     * Coloris sur lequel cette image a été rendue.
     *
     * C'est LUI qui rattache le mockup à sa ligne de panier en multi-coloris :
     * une commande à trois coloris remonte trois fois la même vue, et sans ce
     * champ les images seraient indistinctes. `null` = produit sans déclinaison
     * de couleur, ou serveur d'avant le multi-coloris.
     */
    color: string | null;
    /** Clé de stockage du fichier rendu, quand le serveur l'expose au lieu d'une URL. */
    key: string | null;
    /** URL d'affichage / de téléchargement du mockup. */
    url: string | null;
    /** Zones du produit réellement composées dans cette vue. */
    zones: string[];
}

/** Chaîne exploitable, ou `null` — évite de propager `''` et `'   '` au storefront. */
function readText(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Zones d'une vue, ramenées à des identifiants.
 *
 * Le serveur peut les donner à plat (`['uuid']`) ou en objets (`[{ id }]`) :
 * l'intégrateur, lui, ne doit voir qu'un tableau de chaînes.
 */
function readZones(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((zone) => {
            const asText = readText(zone);
            if (asText) {
                return asText;
            }
            const asObject = zone as { id?: unknown; zoneId?: unknown } | null;
            return readText(asObject?.zoneId) ?? readText(asObject?.id);
        })
        .filter((zone): zone is string => zone !== null);
}

/**
 * Normalise la réponse du serveur de rendu en une liste exploitable telle quelle
 * par le storefront.
 *
 * Une entrée SANS url ni clé est écartée : la boutique n'aurait rien à en
 * afficher, et un tableau « plein » d'entrées inutilisables est plus trompeur
 * qu'un tableau vide — c'est exactement ce que teste
 * `postMockupsGeneratedToParent` pour décider d'émettre ou non.
 */
export function normalizeGeneratedMockups(raw: unknown): GeneratedMockup[] {
    if (!Array.isArray(raw)) {
        return [];
    }

    const mockups: GeneratedMockup[] = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object') {
            continue;
        }

        const item = entry as Record<string, unknown>;
        const url = readText(item.url);
        const key = readText(item.key);
        if (!url && !key) {
            continue;
        }

        mockups.push({
            viewId: readText(item.viewId),
            name: readText(item.name),
            color: readText(item.color),
            key,
            url,
            zones: readZones(item.zones)
        });
    }

    return mockups;
}

/**
 * Regroupe des mockups par coloris, pour les rattacher à leur ligne de commande.
 *
 * La comparaison est INSENSIBLE À LA CASSE et aux espaces : le nom de coloris
 * fait un aller-retour par le storefront e-commerce et le serveur de rendu, sa
 * ponctuation n'est pas garantie identique au retour (c'est déjà l'hypothèse du
 * backend, qui normalise ses clés de fond — cf. MockupView::normalizeColorKey).
 */
export function groupMockupsByColor(mockups: GeneratedMockup[]): Map<string, GeneratedMockup[]> {
    const grouped = new Map<string, GeneratedMockup[]>();
    for (const mockup of mockups) {
        const key = mockupColorKey(mockup.color);
        const bucket = grouped.get(key);
        if (bucket) {
            bucket.push(mockup);
        } else {
            grouped.set(key, [mockup]);
        }
    }
    return grouped;
}

/** Clé de regroupement d'un coloris. `''` = aucun coloris (fond par défaut). */
export function mockupColorKey(color: string | null | undefined): string {
    return typeof color === 'string' ? color.trim().toLowerCase() : '';
}
